import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Truck,
  Store,
  MapPin,
  Users,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';

interface SetupStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const steps: SetupStep[] = [
  {
    id: 'store',
    title: 'Store Connected',
    description: 'Your Shopify store is connected',
    icon: <Store className="w-6 h-6" />,
  },
  {
    id: 'pickup',
    title: 'Pickup Location',
    description: 'Set your package pickup address',
    icon: <MapPin className="w-6 h-6" />,
  },
  {
    id: 'team',
    title: 'Team Members',
    description: 'Add your production team',
    icon: <Users className="w-6 h-6" />,
  },
];

export default function Setup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Pickup location
    pickupAddress: '',
    pickupCity: '',
    pickupState: '',
    pickupZip: '',
    pickupPhone: '',
    pickupInstructions: '',
    // Team member
    teamName: '',
    teamPhone: '',
    teamEmail: '',
  });

  const shop = searchParams.get('shop');
  const installed = searchParams.get('installed') === 'true';

  useEffect(() => {
    if (!shop) {
      navigate('/login');
    }
  }, [shop, navigate]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Save setup data and redirect to dashboard
      try {
        await fetch('/api/setup/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shop, ...formData }),
        });
        navigate('/');
      } catch {
        // Handle error
      }
    }
  };

  const handleSkip = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2">
            <Truck className="w-8 h-8 text-uber-green" />
            <h1 className="text-xl font-bold">UberShipConnect Setup</h1>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  index <= currentStep
                    ? 'bg-uber-green text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {index < currentStep ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  step.icon
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-24 h-1 mx-2 ${
                    index < currentStep ? 'bg-uber-green' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="card">
          <h2 className="text-xl font-bold mb-2">{steps[currentStep].title}</h2>
          <p className="text-gray-600 mb-6">
            {steps[currentStep].description}
          </p>

          {currentStep === 0 && (
            <div className="space-y-4">
              {installed ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">
                      Successfully connected to {shop}
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shopify Store URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="your-store"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <span className="px-4 py-2 bg-gray-100 rounded-lg text-gray-600">
                      .myshopify.com
                    </span>
                  </div>
                  <a
                    href={`/api/shopify/install?shop=${shop || 'your-store.myshopify.com'}`}
                    className="btn-primary inline-block mt-4"
                  >
                    Connect Store
                  </a>
                </div>
              )}
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  name="pickupAddress"
                  value={formData.pickupAddress}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="123 Main Street"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="pickupCity"
                    value={formData.pickupCity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    name="pickupState"
                    value={formData.pickupState}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="CA"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    name="pickupZip"
                    value={formData.pickupZip}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    name="pickupPhone"
                    value={formData.pickupPhone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup Instructions (Optional)
                </label>
                <textarea
                  name="pickupInstructions"
                  value={formData.pickupInstructions}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter through the back door..."
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Add a team member who will receive notifications when orders are
                ready for pickup.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="teamPhone"
                  value={formData.teamPhone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  name="teamEmail"
                  value={formData.teamEmail}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="john@example.com"
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between mt-8">
            <button onClick={handleSkip} className="text-gray-500 hover:text-gray-700">
              {currentStep === steps.length - 1 ? 'Skip for now' : 'Skip'}
            </button>
            <button onClick={handleNext} className="btn-primary flex items-center gap-2">
              {currentStep === steps.length - 1 ? 'Finish Setup' : 'Continue'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
